import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingBag, TrendingUp, Users, AlertCircle, Mail, Package, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

import { admin, type DashboardStats } from '../api/endpoints'
import { Card, CardContent, StatCard } from '../components/Card'
import { PageLoader } from '../components/Spinner'
import { StatusBadge } from '../components/StatusBadge'
import { formatINR, timeAgo } from '../lib/format'

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    admin
      .stats()
      .then((s) => {
        if (!cancelled) setStats(s)
      })
      .catch((err) => {
        toast.error('Failed to load dashboard', { description: err.message })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading || !stats) return <PageLoader />

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label="Today's Orders"
          value={stats.todayOrders}
          icon={<ShoppingBag className="size-5" />}
          hint={`${stats.totalOrders} total all-time`}
        />
        <StatCard
          label="Today's Revenue"
          value={formatINR(stats.todayRevenue)}
          icon={<TrendingUp className="size-5" />}
          hint={`MTD: ${formatINR(stats.mtdRevenue)}`}
        />
        <StatCard
          label="Total Customers"
          value={stats.totalCustomers}
          icon={<Users className="size-5" />}
        />
        <StatCard
          label="Pending Contact"
          value={stats.pendingContact}
          icon={<Mail className="size-5" />}
          hint={stats.pendingContact > 0 ? 'Needs review' : 'All caught up'}
        />
        <StatCard
          label="Low Stock SKUs"
          value={stats.lowStockCount}
          icon={<Package className="size-5" />}
          hint={stats.lowStockCount > 0 ? 'Action needed' : 'Healthy stock'}
        />
        <StatCard
          label="Pending Status"
          value={stats.recentOrders.filter((o) => o.status === 'created').length}
          icon={<AlertCircle className="size-5" />}
          hint="Orders awaiting payment"
        />
      </div>

      <Card>
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-ink">Recent Orders</h2>
          <Link
            to="/orders"
            className="text-xs font-medium text-forest hover:text-forest-dark inline-flex items-center gap-1"
          >
            View all
            <ArrowRight className="size-3" />
          </Link>
        </div>
        {stats.recentOrders.length === 0 ? (
          <CardContent>
            <p className="text-sm text-slatey text-center py-8">No orders yet</p>
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ivory">
                <tr>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                    Order
                  </th>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                    Customer
                  </th>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                    Items
                  </th>
                  <th className="px-5 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slatey">
                    Amount
                  </th>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                    Status
                  </th>
                  <th className="px-5 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slatey">
                    When
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.map((o) => (
                  <tr key={o.id} className="border-t border-line hover:bg-ivory">
                    <td className="px-5 py-3 text-sm">
                      <Link to={`/orders/${o.id}`} className="font-mono text-ink hover:text-forest">
                        {o.orderNumber}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-sm">
                      <p className="text-ink">{o.customerName}</p>
                      <p className="text-xs text-slatey">{o.customerPhone}</p>
                    </td>
                    <td className="px-5 py-3 text-sm text-ink">{o.itemCount}</td>
                    <td className="px-5 py-3 text-sm font-medium text-ink text-right">
                      {formatINR(o.amount)}
                    </td>
                    <td className="px-5 py-3 text-sm">
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="px-5 py-3 text-xs text-slatey text-right">{timeAgo(o.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}